import {
  normalizeStoredActiveOrganization,
  type StoredActiveOrganization,
  type StoredAuthSession,
} from "../config/auth-session";

const TRAILING_SLASHES_RE = /\/+$/u;

type AuthOrganizationApiErrorCode =
  | "organization.auth_revoked"
  | "organization.list_failed"
  | "organization.switch_failed"
  | "organization.response_invalid";

export class AuthOrganizationApiError extends Error {
  readonly code: AuthOrganizationApiErrorCode;
  readonly status: number | null;
  readonly requestId: string | null;
  readonly detailMessage: string | null;

  constructor(input: {
    code: AuthOrganizationApiErrorCode;
    message: string;
    status?: number | null;
    requestId?: string | null;
    detailMessage?: string | null;
  }) {
    super(input.message);
    this.name = "AuthOrganizationApiError";
    this.code = input.code;
    this.status = input.status ?? null;
    this.requestId = input.requestId ?? null;
    this.detailMessage = input.detailMessage ?? null;
  }
}

export async function listAuthOrganizations(
  session: StoredAuthSession
): Promise<StoredActiveOrganization[]> {
  const payload = await fetchAuthOrganizationJson({
    endpoint: toAuthEndpoint(
      session.authBaseUrl,
      "/api/auth/organization/list"
    ),
    operation: "list",
    session,
  });
  return normalizeOrganizationListPayload(payload);
}

export async function setActiveAuthOrganization(input: {
  session: StoredAuthSession;
  organizationSlug: string;
}): Promise<void> {
  await fetchAuthOrganizationJson({
    endpoint: toAuthEndpoint(
      input.session.authBaseUrl,
      "/api/auth/organization/set-active"
    ),
    operation: "switch",
    session: input.session,
    init: {
      method: "POST",
      headers: {
        "content-type": "application/json",
      },
      body: JSON.stringify({
        organizationSlug: input.organizationSlug,
      }),
    },
  });
}

async function fetchAuthOrganizationJson(input: {
  endpoint: string;
  operation: "list" | "switch";
  session: StoredAuthSession;
  init?: RequestInit;
}): Promise<unknown> {
  let response: Response;
  try {
    response = await fetch(input.endpoint, {
      method: "GET",
      ...input.init,
      headers: {
        ...toHeaderRecord(input.init?.headers),
        Authorization: `${normalizeTokenType(input.session.tokenType)} ${input.session.accessToken}`,
      },
    });
  } catch (error) {
    throw new AuthOrganizationApiError({
      code:
        input.operation === "list"
          ? "organization.list_failed"
          : "organization.switch_failed",
      message: "WakeLoop auth service is temporarily unavailable.",
      detailMessage: error instanceof Error ? error.message : String(error),
    });
  }

  const raw = await response.text();
  const payload = parseJsonOrNull(raw);
  if (response.status === 401 || response.status === 403) {
    throw new AuthOrganizationApiError({
      code: "organization.auth_revoked",
      message: "Your WakeLoop sign-in is no longer valid.",
      status: response.status,
      requestId: readRequestId(payload),
      detailMessage: readErrorMessage(payload),
    });
  }
  if (!response.ok) {
    throw new AuthOrganizationApiError({
      code:
        input.operation === "list"
          ? "organization.list_failed"
          : "organization.switch_failed",
      message:
        input.operation === "list"
          ? "Could not load your WakeLoop organizations."
          : "Could not switch the active WakeLoop organization.",
      status: response.status,
      requestId: readRequestId(payload),
      detailMessage: readErrorMessage(payload) ?? raw,
    });
  }
  return payload;
}

function normalizeOrganizationListPayload(
  payload: unknown
): StoredActiveOrganization[] {
  const candidates = resolveOrganizationListCandidates(payload);
  const organizations = candidates
    .map((candidate) => normalizeOrganizationFromPayload(candidate))
    .filter(
      (organization): organization is StoredActiveOrganization =>
        organization !== null
    );
  if (candidates.length > 0 && organizations.length !== candidates.length) {
    throw new AuthOrganizationApiError({
      code: "organization.response_invalid",
      message: "WakeLoop auth returned an invalid organization list.",
    });
  }
  return organizations;
}

function resolveOrganizationListCandidates(payload: unknown): unknown[] {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (!isRecord(payload)) {
    throw new AuthOrganizationApiError({
      code: "organization.response_invalid",
      message: "WakeLoop auth returned an invalid organization list.",
    });
  }
  if (Array.isArray(payload.organizations)) {
    return payload.organizations;
  }
  if (Array.isArray(payload.data)) {
    return payload.data;
  }
  if (Array.isArray(payload.items)) {
    return payload.items;
  }
  throw new AuthOrganizationApiError({
    code: "organization.response_invalid",
    message: "WakeLoop auth returned an invalid organization list.",
  });
}

function normalizeOrganizationFromPayload(
  payload: unknown
): StoredActiveOrganization | null {
  const direct = normalizeStoredActiveOrganization(payload);
  if (direct) {
    return direct;
  }
  if (!isRecord(payload)) {
    return null;
  }
  const organization = normalizeStoredActiveOrganization(payload.organization);
  if (!organization) {
    return null;
  }
  const role =
    normalizeOptionalString(payload.role) ??
    (isRecord(payload.member)
      ? normalizeOptionalString(payload.member.role)
      : null);
  return {
    ...organization,
    ...(role ? { role } : {}),
  };
}

function normalizeTokenType(value: string | undefined): string {
  const normalized = String(value ?? "").trim();
  return normalized.length > 0 ? normalized : "Bearer";
}

function toHeaderRecord(
  headers: RequestInit["headers"] | undefined
): Record<string, string> {
  if (!headers) {
    return {};
  }
  if (headers instanceof Headers) {
    const record: Record<string, string> = {};
    headers.forEach((value, key) => {
      record[key] = value;
    });
    return record;
  }
  if (Array.isArray(headers)) {
    return Object.fromEntries(headers);
  }
  const record: Record<string, string> = {};
  for (const [key, value] of Object.entries(headers)) {
    if (typeof value === "string") {
      record[key] = value;
    }
  }
  return record;
}

function toAuthEndpoint(baseUrl: string, pathname: string): string {
  const url = new URL(baseUrl);
  const normalizedBasePath = url.pathname.replace(TRAILING_SLASHES_RE, "");
  const normalizedPathname = pathname.startsWith("/")
    ? pathname
    : `/${pathname}`;
  url.pathname = `${normalizedBasePath}${normalizedPathname}` || "/";
  url.search = "";
  url.hash = "";
  return url.toString();
}

function parseJsonOrNull(raw: string): unknown {
  if (raw.trim().length === 0) {
    return null;
  }
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    return null;
  }
}

function readRequestId(payload: unknown): string | null {
  if (!isRecord(payload)) {
    return null;
  }
  return (
    normalizeOptionalString(payload.requestId) ??
    (isRecord(payload.error)
      ? normalizeOptionalString(payload.error.requestId)
      : null)
  );
}

function readErrorMessage(payload: unknown): string | null {
  if (!isRecord(payload)) {
    return null;
  }
  return (
    normalizeOptionalString(payload.message) ??
    (isRecord(payload.error)
      ? normalizeOptionalString(payload.error.message)
      : null)
  );
}

function normalizeOptionalString(value: unknown): string | null {
  const normalized = String(value ?? "").trim();
  return normalized.length > 0 ? normalized : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
